import {
  useEffect,
  useId,
  useState,
  type FC,
  type FormEvent,
  type ReactNode
} from "react";

import {
  AuthButton,
  AuthCardFooter,
  AuthField,
  AuthFooterAction,
  AuthHeader,
  AuthShell
} from "@/features/login";

import { ClientPortalAPI } from "../api";
import { LanguageMenu } from "../components/ClientPortalShell";
import {
  initialPortalLanguage,
  languageDirection,
  portalI18n,
  usePortalT
} from "../i18n";

/** The signed-out frame: the auth card, with the language switch top-right. */
export const PublicPortalFrame: FC<{ children: ReactNode }> = ({
  children
}) => {
  const { i18n } = usePortalT();
  useEffect(() => {
    void portalI18n.changeLanguage(initialPortalLanguage(null));
  }, []);
  return (
    <div dir={languageDirection(i18n.language)} lang={i18n.language}>
      <AuthShell topRight={<LanguageMenu />} legal={false}>
        {children}
      </AuthShell>
    </div>
  );
};

/**
 * Title, then a request for a sign-in link. Once sent, the card says so in the
 * same words whether or not the email has a portal, so it can't be used to
 * find out who is a client.
 */
export const SignInLinkCard: FC<{
  title: string;
  subtitle?: string;
  footer?: ReactNode;
}> = ({ title, subtitle, footer }) => {
  const { t } = usePortalT();
  const id = useId();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = (e: FormEvent) => {
    e.preventDefault();
    const address = email.trim();
    if (!address || sending) return;
    setSending(true);
    setError(null);
    ClientPortalAPI.sendSignInLink(address)
      .then(() => setSent(true))
      .catch(() => setError(t("signIn.failed")))
      .finally(() => setSending(false));
  };

  if (sent) {
    return (
      <>
        <div role="status">
          <AuthHeader
            title={t("signIn.sentTitle")}
            subtitle={t("signIn.sent", { email: email.trim() })}
          />
        </div>
        <div className="mt-11 flex flex-col">
          <AuthButton
            variant="secondary"
            fullWidth
            onClick={() => setSent(false)}
          >
            {t("signIn.sendAnother")}
          </AuthButton>
          {footer}
        </div>
      </>
    );
  }
  return (
    <>
      <AuthHeader title={title} subtitle={subtitle} />
      <form className="mt-11 flex flex-col" noValidate onSubmit={send}>
        <div className="flex flex-col gap-5">
          <AuthField
            id={`${id}-email`}
            label={t("signIn.email")}
            type="email"
            autoFocus
            autoComplete="email"
            inputMode="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            error={error}
          />
          <AuthButton
            type="submit"
            fullWidth
            forward
            loading={sending}
            loadingText={t("signIn.sending")}
          >
            {t("signIn.send")}
          </AuthButton>
        </div>
        {footer}
      </form>
    </>
  );
};

export const PortalSignInPage: FC = () => {
  const { t } = usePortalT();
  return (
    <PublicPortalFrame>
      <div className="flex flex-col" data-testid="client-portal-sign-in">
        <SignInLinkCard
          title={t("signIn.title")}
          subtitle={t("signIn.hint")}
          footer={
            <AuthCardFooter lead={t("signIn.staff")}>
              <AuthFooterAction to="/login">
                {t("signIn.staffLink")}
              </AuthFooterAction>
            </AuthCardFooter>
          }
        />
      </div>
    </PublicPortalFrame>
  );
};
